import { tx } from "@/i18n/tx";
import { useEffect, useState } from "react";
import { X, Share, Plus, Download } from "lucide-react";

const DISMISS_KEY = "oresto-pwa-install-dismissed-at";
const DISMISS_DAYS = 14;
const SHOW_AFTER_MS = 8000; // let the user use the app a bit before nudging

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (!Number.isFinite(at)) return false;
    const days = (Date.now() - at) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (wasRecentlyDismissed()) return;

    // Android / desktop Chrome / Edge
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS Safari — no beforeinstallprompt; show manual instructions
    if (isIOS() && isSafari()) {
      const t = setTimeout(() => {
        setIosMode(true);
        setVisible(true);
      }, SHOW_AFTER_MS);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBIP);
      };
    }

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {}
    setDeferred(null);
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed inset-x-3 bottom-3 z-[9999] md:left-auto md:right-4 md:bottom-4 md:max-w-sm"
    >
      <div className="rounded-2xl bg-white text-neutral-900 shadow-2xl ring-1 ring-black/10 dark:bg-neutral-900 dark:text-neutral-50 dark:ring-white/10">
        <div className="flex items-start gap-3 p-4">
          <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[#2A7D5F] text-white">
            <Download className="h-5 w-5" />
          </div>
          <div className="flex-1 text-sm">
            <p className="font-semibold">{tx("ui_pwainstallprompt.k3185b0")}</p>
            {iosMode ? (
              <p className="mt-1 text-neutral-600 dark:text-neutral-300">
                Appuyez sur{" "}
                <Share className="mx-0.5 inline h-4 w-4 align-text-bottom" />
                <span className="font-medium">{tx("ui_pwainstallprompt.k7f749d")}</span>, puis
                <span className="font-medium"> {tx("ui_pwainstallprompt.k90c1bf")}</span>{" "}
                <Plus className="mx-0.5 inline h-4 w-4 align-text-bottom" />.
              </p>
            ) : (
              <p className="mt-1 text-neutral-600 dark:text-neutral-300">
                {tx("ui_pwainstallprompt.ke5c3f4")}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="pwa-install-dismiss"
            aria-label={tx("ui_pwainstallprompt.k7d2062")}
            className="flex-none rounded-lg p-1 text-neutral-400 hover:bg-black/5 hover:text-neutral-700 dark:hover:bg-white/10 dark:hover:text-neutral-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {!iosMode && (
          <div className="flex items-center justify-end gap-2 border-t border-black/5 px-4 py-2.5 dark:border-white/10">
            <button
              type="button"
              onClick={dismiss}
              data-testid="pwa-install-later"
              className="rounded-lg px-3 py-1.5 text-sm text-neutral-600 hover:bg-black/5 dark:text-neutral-300 dark:hover:bg-white/10"
            >
              {tx("ui_pwainstallprompt.k4fa7fc")}
            </button>
            <button
              type="button"
              onClick={install}
              data-testid="pwa-install-confirm"
              className="rounded-lg bg-[#2A7D5F] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#256b52]"
            >
              {tx("ui_pwainstallprompt.kd1be37")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
